import { ChakraProvider } from "@chakra-ui/react";
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import {
  RESET_PASSWORD_ROUTE,
  SET_PASSWORD_ROUTE,
  configureCoreAPI,
} from "services/coreapi";
import { AuthStoreProvider } from "stores/AuthStoreProvider";
import { NotificationStoreProvider } from "stores/NotificationStoreProvider";
import { BrandProvider } from "brand/BrandConfig";
import { RouteGuard } from "router/RouteGuard";
import { AuthCallbackPage } from "pages/AuthCallbackPage";
import { LogoutPage } from "pages/LogoutPage";
import { LoginPage, ResetPasswordPage, SetPasswordPage } from "pages/LoginPage";

import { system } from "./theme";
import { truelovelicaBrand } from "./brand.config";
import { NoTrackingServices } from "./services/NoTracking";
import { AuthenticatedLayout } from "./layouts/AuthenticatedLayout";
import { HomePage } from "./pages/HomePage";
import { ChatPage } from "./pages/ChatPage";
import { ChatsPage } from "./pages/ChatsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { MembershipPage } from "./pages/MembershipPage";
import { SubscribePage } from "./pages/SubscribePage";
import { OnboardingPage } from "./pages/OnboardingPage";

// Must run before anything calls `coreapi` (the clients are built lazily on
// first use, so module scope — ahead of createBrowserRouter — is early enough).
configureCoreAPI(truelovelicaBrand);

/**
 * Dark everywhere: core's login, password and loading screens are drawn
 * for a dark page and sit on the theme's dark body as they are.
 */
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      errorElement={<Navigate to="/login" replace />}
      element={
        <BrandProvider brand={truelovelicaBrand}>
          <ChakraProvider value={system}>
            <NotificationStoreProvider>
              <NoTrackingServices>
                <AuthStoreProvider>
                  {/* No age gate: general-audience app. */}
                  <RouteGuard
                    requiresConsent={false}
                    requiresSubscription={true}
                    onboardingRoute="/onboarding"
                  >
                    <Outlet />
                  </RouteGuard>
                </AuthStoreProvider>
              </NoTrackingServices>
            </NotificationStoreProvider>
          </ChakraProvider>
        </BrandProvider>
      }
    >
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path={RESET_PASSWORD_ROUTE} element={<ResetPasswordPage />} />
      {/* RouteGuard parks a logged-in user with no password here. */}
      <Route path={SET_PASSWORD_ROUTE} element={<SetPasswordPage />} />
      {/* Reachable in every auth state (RouteGuard lets it through). */}
      <Route path="/logout" element={<LogoutPage />} />
      {/* RouteGuard parks users without an active membership here. */}
      <Route path="/subscribe" element={<SubscribePage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route element={<AuthenticatedLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/chats" element={<ChatsPage />} />
        <Route path="/chat/:companionID" element={<ChatPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/membership" element={<MembershipPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);
